/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export type xGAHandlerType = {
    checkoutFlowEvent?: boolean;
    checkoutFlowEventName?: string;
    addToCartEvent?: boolean;
    addToCartEventName?: string;
    cartToBuyRateEvent?: boolean;
    cartToBuyRateEventName?: string;
    removeFromCartLineEvent?: boolean;
    removeFromCartLineEventName?: string;
    pdpViewEvent?: boolean;
    pdpViewEventName?: string;
    productImpressionEvent?: boolean;
    productImpressionEventName?: string;
    productBrand?: boolean;
    productCategory?: boolean;
};

export type xGAStateType = Readonly<xGAHandlerType>;

const initialXGA: xGAStateType = {
    checkoutFlowEvent: false,
    checkoutFlowEventName: "checkout",
    addToCartEvent: false,
    addToCartEventName: "addToCart",
    cartToBuyRateEvent: false,
    cartToBuyRateEventName: "purchase",
    removeFromCartLineEvent: false,
    removeFromCartLineEventName: "removeFromCart",
    pdpViewEvent: false,
    pdpViewEventName: "productDetails",
    productImpressionEvent: false,
    productImpressionEventName: "productImpression",
    productBrand: false,
    productCategory: false,
} as xGAStateType;

const reducer = {
    "xGA/update": (draft: Draft<xGAStateType>, action: { data: xGAHandlerType }) => {
        draft["checkoutFlowEvent"] = action.data.checkoutFlowEvent ?? false;
        draft["checkoutFlowEventName"] = action.data.checkoutFlowEventName ?? "checkout";
        draft["addToCartEvent"] = action.data.addToCartEvent ?? false;
        draft["addToCartEventName"] = action.data.addToCartEventName ?? "addToCart";
        draft["cartToBuyRateEvent"] = action.data.cartToBuyRateEvent ?? false;
        draft["cartToBuyRateEventName"] = action.data.cartToBuyRateEventName ?? "purchase";
        draft["removeFromCartLineEvent"] = action.data.removeFromCartLineEvent ?? false;
        draft["removeFromCartLineEventName"] = action.data.removeFromCartLineEventName ?? "removeFromCart";
        draft["pdpViewEvent"] = action.data.pdpViewEvent ?? false;
        draft["pdpViewEventName"] = action.data.pdpViewEventName ?? "productDetails";
        draft["productImpressionEvent"] = action.data.productImpressionEvent ?? false;
        draft["productImpressionEventName"] = action.data.productImpressionEventName ?? "productImpression";
        draft["productBrand"] = action.data.productBrand ?? false;
        draft["productCategory"] = action.data.productCategory ?? false;
    },
};

const XGA = createTypedReducerWithImmer(initialXGA, reducer);

(reducers as any).xGA = XGA;

export type xGaState = {
    xGA: xGAStateType;
} & ApplicationState;

type xGAReducers = {
    [key: string]: typeof XGA;
};

export type xGAActions = Parameters<xGAReducers[keyof xGAReducers]>[1] | AnyAction;
